import DOMPurify from 'dompurify';

/**
 * Sanitization profiles.
 *
 * RICH         — CMS/Contentful authored content (curriculum, static pages, modals).
 *                Allows embeds (iframe/video/audio), the checkbox markup the
 *                curriculum helpers rely on and plain path-only icons.
 * USER_CONTENT — anything a user can type (answers, comments, project reflections).
 *                Same formatting tags, but no embeds and no form controls.
 */
export const SanitizeProfile = {
  RICH: 'rich',
  USER_CONTENT: 'userContent',
};

// Formatting/structure tags shared by every profile.
const BASE_TAGS = [
  'a', 'abbr', 'address', 'article', 'aside', 'b', 'bdi', 'bdo', 'blockquote', 'br',
  'caption', 'cite', 'code', 'col', 'colgroup', 'dd', 'del', 'details', 'div', 'dl',
  'dt', 'em', 'figcaption', 'figure', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'header', 'hr', 'i', 'img', 'ins', 'kbd', 'li', 'main', 'mark', 'nav', 'ol', 'p',
  'pre', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'section', 'small', 'span', 'strong',
  'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'time',
  'tr', 'u', 'ul', 'var', 'wbr',
];

// Contentful curriculum content ships checkboxes that `curriculumHelper` rehydrates.
const FORM_TAGS = ['input', 'label'];

const EMBED_TAGS = ['iframe', 'video', 'audio', 'source', 'track', 'picture'];

// Path-only icons, e.g. the curriculum image fallback; no use/foreignObject/animation.
const ICON_TAGS = ['svg', 'path'];

const BASE_ATTR = [
  'align', 'alt', 'aria-hidden', 'aria-label', 'aria-labelledby', 'border', 'cellpadding',
  'cellspacing', 'cite', 'class', 'colspan', 'datetime', 'decoding', 'dir', 'headers', 'height',
  'href', 'hreflang', 'id', 'lang', 'loading', 'reversed', 'rel', 'role', 'rowspan',
  'scope', 'sizes', 'span', 'src', 'srcset', 'start', 'style', 'tabindex', 'target',
  'title', 'valign', 'width',
];

const FORM_ATTR = ['checked', 'disabled', 'for', 'name', 'readonly', 'type', 'value'];

const EMBED_ATTR = [
  'allow', 'allowfullscreen', 'controls', 'frameborder', 'loop', 'muted', 'playsinline',
  'poster', 'preload', 'referrerpolicy', 'sandbox', 'scrolling',
];

const ICON_ATTR = [
  'd', 'fill', 'focusable', 'stroke', 'stroke-linecap', 'stroke-linejoin', 'stroke-width',
  'viewbox', 'xmlns',
];

// Never allowed. `iframe` and `svg` are added for USER_CONTENT only — the RICH
// profile keeps embeds (see the https check in the hooks below) and icons.
const FORBID_TAGS = [
  'script', 'style', 'object', 'embed', 'form', 'button', 'base', 'link',
  'meta', 'noscript', 'template', 'math',
];

// Shared by every profile — belt and braces on top of the tag/attr allowlists.
const BASE_CONFIG = {
  FORBID_TAGS,
  FORBID_ATTR: ['srcdoc', 'formaction', 'action', 'ping', 'xlink:href'],
  // http(s), mail/phone links, anchors, relative URLs and inline raster images.
  // Notably excludes javascript:, data:text/html and data:image/svg+xml.
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel):|data:image\/(?:png|jpe?g|gif|webp);base64,|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  ALLOW_DATA_ATTR: true,
  ALLOW_ARIA_ATTR: true,
};

const CONFIGS = {
  [SanitizeProfile.RICH]: {
    ...BASE_CONFIG,
    ALLOWED_TAGS: [...BASE_TAGS, ...FORM_TAGS, ...EMBED_TAGS, ...ICON_TAGS],
    ALLOWED_ATTR: [...BASE_ATTR, ...FORM_ATTR, ...EMBED_ATTR, ...ICON_ATTR],
  },
  [SanitizeProfile.USER_CONTENT]: {
    ...BASE_CONFIG,
    ALLOWED_TAGS: BASE_TAGS,
    ALLOWED_ATTR: BASE_ATTR,
    FORBID_TAGS: [...FORBID_TAGS, 'iframe', 'svg'],
  },
};

// Embeds must be https, or protocol-relative (which resolves to https on an
// https page). The point is to keep javascript:/data: iframes out even if the
// URI regexp ever loosens — the host is deliberately not restricted, because
// curriculum authors embed arbitrary tools (trinket, video, and so on).
const isSafeEmbedSrc = (src) => /^https:\/\//i.test(src) || /^\/\//.test(src);

// `_self`, `_parent` and `_top` reuse the current browsing context, so they
// carry no tabnabbing risk — and rewriting their rel would clobber whatever the
// author set. Anything else, `_blank` or a named target, opens a new one.
const SAME_CONTEXT_TARGETS = new Set(['_self', '_parent', '_top']);

const opensNewBrowsingContext = (target) => {
  const value = String(target || '').trim().toLowerCase();

  return value !== '' && !SAME_CONTEXT_TARGETS.has(value);
};

// DOMPurify lets any `data:` URI through on media tags (img/video/audio/source/track),
// which would allow a scriptable data:image/svg+xml. Restrict it to raster images.
const ALLOWED_DATA_URI = /^data:image\/(?:png|jpe?g|gif|webp);base64,/i;
const URI_ATTRIBUTES = ['src', 'href', 'srcset', 'poster'];

let hooksRegistered = false;

const registerHooks = () => {
  if (hooksRegistered) return;
  hooksRegistered = true;

  // Drop embeds that are not plain https before their attributes are processed.
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe' || typeof node.getAttribute !== 'function') return;

    if (!isSafeEmbedSrc(node.getAttribute('src') || '')) {
      if (node.parentNode) node.parentNode.removeChild(node);
      return;
    }

    // Fallback content is only shown when iframes are unsupported, and DOMPurify's
    // mXSS guard drops the whole embed when that content looks like markup.
    node.textContent = '';
  });

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (typeof node.getAttribute !== 'function') return;

    // Reverse tabnabbing protection for links that open a new browsing context.
    if (node.tagName === 'A' && opensNewBrowsingContext(node.getAttribute('target'))) {
      node.setAttribute('rel', 'noopener noreferrer');
    }

    URI_ATTRIBUTES.forEach(attribute => {
      const value = node.getAttribute(attribute);

      if (value && /^\s*data:/i.test(value) && !ALLOWED_DATA_URI.test(value.trim())) {
        node.removeAttribute(attribute);
      }
    });
  });
};

const escapeHtml = (value) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Sanitizes an HTML string before it reaches `dangerouslySetInnerHTML`.
 *
 * @param {string} htmlString raw HTML from Contentful, the API or a user
 * @param {string} profile one of `SanitizeProfile`
 * @returns {string} sanitized HTML, never `null`/`undefined`
 */
export const sanitizeHtml = (htmlString, profile = SanitizeProfile.RICH) => {
  if (htmlString == null || htmlString === '') return '';

  const value = typeof htmlString === 'string' ? htmlString : String(htmlString);

  // No DOM to sanitize against (non-browser runtime): degrade to plain text
  // instead of returning the markup untouched.
  if (!DOMPurify.isSupported) return escapeHtml(value);

  registerHooks();

  return DOMPurify.sanitize(value, CONFIGS[profile] || CONFIGS[SanitizeProfile.RICH]);
};
